import { weekFileName } from '../../lib/weekFileName';
import { DocLangToggle } from '../ui/doc-lang-toggle';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  FileText, Camera, CheckSquare, Plus, Trash2, Upload, 
  Eye, Edit, Save, X, AlertCircle, CheckCircle, HelpCircle,
  FileSignature, Image as ImageIcon, Download, FileDown
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { DateText } from '../ui/week-badge';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const CHECKLIST_STATUS = [
  { value: 'pending', label: i18n.t('auto.lbl.pending'), color: 'bg-gray-100 text-gray-800', icon: HelpCircle },
  { value: 'pass', label: i18n.t('auto.lbl.pass'), color: 'bg-green-100 text-green-800', icon: CheckCircle },
  { value: 'fail', label: i18n.t('auto.lbl.fail'), color: 'bg-red-100 text-red-800', icon: X },
  { value: 'na', label: 'N/A', color: 'bg-blue-100 text-blue-800', icon: HelpCircle },
  { value: 'observation', label: i18n.t('auto.lbl.observation'), color: 'bg-yellow-100 text-yellow-800', icon: AlertCircle },
];

// Default ISO 9001 checklist template
const DEFAULT_CHECKLIST_TEMPLATE = [
  { clause: '4.1', requirement: 'Understanding the organization and its context' },
  { clause: '4.2', requirement: 'Understanding the needs and expectations of interested parties' },
  { clause: '4.3', requirement: 'Determining the scope of the QMS' },
  { clause: '4.4', requirement: 'Quality management system and its processes' },
  { clause: '5.1', requirement: 'Leadership and commitment' },
  { clause: '5.2', requirement: 'Quality policy' },
  { clause: '5.3', requirement: 'Organizational roles, responsibilities and authorities' },
  { clause: '6.1', requirement: 'Actions to address risks and opportunities' },
  { clause: '6.2', requirement: 'Quality objectives and planning to achieve them' },
  { clause: '6.3', requirement: 'Planning of changes' },
  { clause: '7.1', requirement: 'Resources' },
  { clause: '7.2', requirement: 'Competence' },
  { clause: '7.3', requirement: 'Awareness' },
  { clause: '7.4', requirement: 'Communication' },
  { clause: '7.5', requirement: 'Documented information' },
  { clause: '8.1', requirement: 'Operational planning and control' },
  { clause: '8.2', requirement: 'Requirements for products and services' },
  { clause: '8.3', requirement: 'Design and development' },
  { clause: '8.4', requirement: 'Control of externally provided processes, products and services' },
  { clause: '8.5', requirement: 'Production and service provision' },
  { clause: '8.6', requirement: 'Release of products and services' },
  { clause: '8.7', requirement: 'Control of nonconforming outputs' },
  { clause: '9.1', requirement: 'Monitoring, measurement, analysis and evaluation' },
  { clause: '9.2', requirement: 'Internal audit' },
  { clause: '9.3', requirement: 'Management review' },
  { clause: '10.1', requirement: 'General - Improvement' },
  { clause: '10.2', requirement: 'Nonconformity and corrective action' },
  { clause: '10.3', requirement: 'Continual improvement' },
];

const AuditReport = ({ audit, token, onClose, onSave }) => {
  const { t } = useTranslation();
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('summary');
  const [showPhotoDialog, setShowPhotoDialog] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoUrls, setPhotoUrls] = useState({});
  
  // Form state
  const [executiveSummary, setExecutiveSummary] = useState('');
  const [observations, setObservations] = useState('');
  const [recommendations, setRecommendations] = useState('');
  const [checklistItems, setChecklistItems] = useState([]);
  const [photos, setPhotos] = useState([]);

  // Fetch report
  const fetchReport = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/iso/audits/${audit.id}/report`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setReport(data);
        setExecutiveSummary(data.executive_summary || '');
        setObservations(data.observations || '');
        setRecommendations(data.recommendations || '');
        setChecklistItems(data.checklist_items || []);
        setPhotos(data.photos || []);
      } else if (response.status === 404) {
        // No report exists, initialize with template
        setReport(null);
        setChecklistItems(DEFAULT_CHECKLIST_TEMPLATE.map(item => ({
          id: `${Date.now()}-${Math.random()}`,
          clause: item.clause,
          requirement: item.requirement,
          status: 'pending',
          evidence: '',
          notes: '',
          photos: [],
        })));
      }
    } catch (error) {
      console.error('Failed to fetch report:', error);
      toast.error(t('auto.iso.failedToLoadReport'));
    } finally {
      setIsLoading(false);
    }
  }, [audit.id, token]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // Load photo blob URLs via authenticated fetch
  useEffect(() => {
    photos.forEach(async (photo) => {
      if (photoUrls[photo.filename]) return;
      try {
        const response = await fetch(`${API_BASE}/api/iso/audit-photos/${photo.filename}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (response.ok) {
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          setPhotoUrls(prev => ({ ...prev, [photo.filename]: url }));
        }
      } catch (err) {
        console.error('Failed to load audit photo:', err);
      }
    });
  }, [photos, token, photoUrls]);

  // Create or update report
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const reportData = {
        audit_id: audit.id,
        executive_summary: executiveSummary,
        checklist_items: checklistItems,
        observations,
        recommendations,
      };

      const method = report ? t('auto.iso.put') : t('auto.iso.post');
      const response = await fetch(`${API_BASE}/api/iso/audits/${audit.id}/report`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(reportData),
      });

      if (!response.ok) {
        throw new Error('Failed to save report');
      }

      toast.success(t('auto.iso.reportSavedSuccessfully'));
      fetchReport();
      if (onSave) onSave();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(t('auto.iso.failedToSaveReport'));
    } finally {
      setIsSaving(false);
    }
  };

  // Handle photo upload
  const handlePhotoUpload = async (e, checklistItemId = null) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!report) {
      toast.error(t('auto.iso.pleaseSaveTheReportFirstBefore'));
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    if (checklistItemId) {
      formData.append('checklist_item_id', checklistItemId);
    }

    try {
      const response = await fetch(`${API_BASE}/api/iso/audits/${audit.id}/report/photos`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      toast.success(t('auto.iso.photoUploaded'));
      fetchReport();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(t('auto.iso.failedToUploadPhoto'));
    }
  };

  // Delete photo
  const handleDeletePhoto = async (photoId) => {
    try {
      const response = await fetch(`${API_BASE}/api/iso/audits/${audit.id}/report/photos/${photoId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Delete failed');
      }

      toast.success(t('auto.iso.photoDeleted'));
      fetchReport();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(t('auto.iso.failedToDeletePhoto'));
    }
  };

  // Update checklist item
  const updateChecklistItem = (index, field, value) => {
    const updated = [...checklistItems];
    updated[index] = { ...updated[index], [field]: value };
    setChecklistItems(updated);
  };

  // Add checklist item
  const addChecklistItem = () => {
    setChecklistItems([
      ...checklistItems,
      {
        id: `${Date.now()}-${Math.random()}`,
        clause: '',
        requirement: '',
        status: 'pending',
        evidence: '',
        notes: '',
        photos: [],
      },
    ]);
  };

  // Remove checklist item
  const removeChecklistItem = (index) => {
    setChecklistItems(checklistItems.filter((_, i) => i !== index));
  };

  // Calculate conformity score
  const calculateConformityScore = () => {
    const assessed = checklistItems.filter(item => item.status !== 'pending' && item.status !== 'na');
    if (assessed.length === 0) return 0;
    const passing = assessed.filter(item => item.status === 'pass' || item.status === 'observation');
    return Math.round((passing.length / assessed.length) * 100);
  };

  const getStatusBadge = (status) => {
    const statusInfo = CHECKLIST_STATUS.find(s => s.value === status);
    if (!statusInfo) return null;
    const Icon = statusInfo.icon;
    return (
      <Badge className={`${statusInfo.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {statusInfo.label}
      </Badge>
    );
  };

  // Export to PDF
  const handleExportPdf = async () => {
    if (!report) {
      toast.error(t('auto.iso.pleaseSaveTheReportFirstBefore2'));
      return;
    }
    
    try {
      toast.info(t('auto.iso.generatingPdf'));
      const response = await fetch(`${API_BASE}/api/iso/audits/${audit.id}/report/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to generate PDF');
      }
      
      // Get the PDF blob and download it
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = weekFileName(`Audit_Report_${audit.audit_number || audit.id}.pdf`);
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(t('auto.iso.pdfExportedSuccessfully'));
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error(error.message || 'Failed to export PDF');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">{t('auto.iso.loadingReport')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="audit-report">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">{t('auto.iso.auditReport')}</h2>
          <p className="text-sm text-gray-500">
            {audit.audit_number} - {audit.title}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline">
            Conformity: {calculateConformityScore()}%
          </Badge>
          <DocLangToggle testId="doc-lang-auditreport-0" />
          <Button 
            variant="outline" 
            onClick={handleExportPdf}
            disabled={!report}
            data-testid="export-pdf-btn"
          >
            <FileDown className="h-4 w-4 mr-2" />
            {t('auto.iso.exportPdf')}
          </Button>
          <Button onClick={handleSave} disabled={isSaving} data-testid="btn-save">
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? t('auto.iso.saving') : t('auto.iso.saveReport')}
          </Button>
          {onClose && (
            <Button variant="outline" onClick={onClose} data-testid="btn-close">
              {t('auto.iso.close')}
            </Button>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="summary" className="flex items-center gap-2" data-testid="tab-summary">
            <FileText className="h-4 w-4" />
            {t('auto.iso.summary')}
          </TabsTrigger>
          <TabsTrigger value="checklist" className="flex items-center gap-2" data-testid="tab-checklist">
            <CheckSquare className="h-4 w-4" />
            Checklist ({checklistItems.length})
          </TabsTrigger>
          <TabsTrigger value="photos" className="flex items-center gap-2" data-testid="tab-photos">
            <Camera className="h-4 w-4" />
            Photos ({photos.length})
          </TabsTrigger>
        </TabsList>

        {/* Summary Tab */}
        <TabsContent value="summary" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('auto.iso.executiveSummary')}</CardTitle>
            </CardHeader>
            <CardContent>
              <textarea
                className="w-full min-h-[120px] p-3 border rounded-md text-sm"
                placeholder={t('auto.iso.provideAnExecutiveSummaryOfThe')}
                value={executiveSummary}
                onChange={(e) => setExecutiveSummary(e.target.value)}
              />
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('auto.iso.observations')}</CardTitle>
              </CardHeader>
              <CardContent>
                <textarea
                  className="w-full min-h-[100px] p-3 border rounded-md text-sm"
                  placeholder={t('auto.iso.keyObservationsFromTheAudit')}
                  value={observations}
                  onChange={(e) => setObservations(e.target.value)}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('auto.iso.recommendations')}</CardTitle>
              </CardHeader>
              <CardContent>
                <textarea
                  className="w-full min-h-[100px] p-3 border rounded-md text-sm"
                  placeholder={t('auto.iso.recommendationsForImprovement')}
                  value={recommendations}
                  onChange={(e) => setRecommendations(e.target.value)}
                />
              </CardContent>
            </Card>
          </div>

          {/* Conformity Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('auto.iso.checklistSummary')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-5 gap-4 text-center">
                {CHECKLIST_STATUS.map(status => {
                  const count = checklistItems.filter(item => item.status === status.value).length;
                  const Icon = status.icon;
                  return (
                    <div key={status.value} className="p-3 rounded-lg bg-gray-50">
                      <div className={`flex items-center justify-center gap-1 ${status.color.split(' ')[1]}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="text-2xl font-bold mt-1">{count}</div>
                      <div className="text-xs text-gray-500">{status.label}</div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Checklist Tab */}
        <TabsContent value="checklist" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-500">
              {t('auto.iso.reviewEachIso9001ClauseAnd')}
            </p>
            <Button onClick={addChecklistItem} variant="outline" size="sm" data-testid="btn-addchecklistitem">
              <Plus className="h-4 w-4 mr-1" />
              {t('auto.iso.addItem')}
            </Button>
          </div>

          <div className="space-y-3">
            {checklistItems.map((item, index) => (
              <Card key={item.id || index} className="overflow-hidden">
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Clause & Requirement */}
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <Input
                          className="w-20"
                          placeholder={t('auto.iso.clause')}
                          value={item.clause}
                          onChange={(e) => updateChecklistItem(index, 'clause', e.target.value)}
                        />
                        <Input
                          className="flex-1"
                          placeholder={t('auto.iso.requirementBeingChecked')}
                          value={item.requirement}
                          onChange={(e) => updateChecklistItem(index, 'requirement', e.target.value)}
                        />
                        <Select
                          value={item.status}
                          onValueChange={(value) => updateChecklistItem(index, 'status', value)}
                        >
                          <SelectTrigger className="w-[130px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CHECKLIST_STATUS.map(s => (
                              <SelectItem key={s.value} value={s.value}>
                                {s.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500"
                          onClick={() => removeChecklistItem(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <Input
                          placeholder={t('auto.iso.evidenceObserved')}
                          value={item.evidence || ''}
                          onChange={(e) => updateChecklistItem(index, 'evidence', e.target.value)}
                        />
                        <Input
                          placeholder={t('auto.iso.notesComments')}
                          value={item.notes || ''}
                          onChange={(e) => updateChecklistItem(index, 'notes', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Photos Tab */}
        <TabsContent value="photos" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-500">
              {t('auto.iso.attachPhotosAsEvidenceForThe')}
            </p>
            <div>
              <input
                type="file"
                id="photo-upload"
                accept="image/*"
                className="hidden"
                onChange={(e) => handlePhotoUpload(e)}
              />
              <Button asChild variant="outline" size="sm">
                <label htmlFor="photo-upload" className="cursor-pointer">
                  <Upload className="h-4 w-4 mr-1" />
                  {t('auto.iso.uploadPhoto')}
                </label>
              </Button>
            </div>
          </div>

          {photos.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-gray-500">
                <Camera className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>{t('auto.iso.noPhotosAttachedYet')}</p>
                <p className="text-sm">{t('auto.iso.uploadPhotosToDocumentAuditEvidence')}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {photos.map((photo) => (
                <Card key={photo.id} className="overflow-hidden group relative">
                  <div className="aspect-square bg-gray-100 flex items-center justify-center">
                    <img
                      src={photoUrls[photo.filename] || ''}
                      alt={photo.description || 'Audit photo'}
                      className="w-full h-full object-cover cursor-pointer"
                      onClick={() => {
                        setSelectedPhoto(photo);
                        setShowPhotoDialog(true);
                      }}
                    />
                  </div>
                  <div className="p-2">
                    <p className="text-xs text-gray-500 truncate">
                      {photo.original_name}
                    </p>
                    <p className="text-xs text-gray-400">
                      {<DateText value={photo.uploaded_at} />}
                    </p>
                  </div>
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      variant="destructive"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => handleDeletePhoto(photo.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Photo Preview Dialog */}
      <Dialog open={showPhotoDialog} onOpenChange={setShowPhotoDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{selectedPhoto?.original_name}</DialogTitle>
            <DialogDescription className="sr-only">{selectedPhoto?.original_name}</DialogDescription>
          </DialogHeader>
          {selectedPhoto && (
            <div className="flex items-center justify-center">
              <img
                src={photoUrls[selectedPhoto.filename] || ''}
                alt={selectedPhoto.description || 'Audit photo'}
                className="max-h-[60vh] object-contain"
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPhotoDialog(false)} data-testid="dialog-close-btn">
              {t('auto.iso.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuditReport;
